import { CHECKLIST_LIMITS, effectiveShifts, isOvernightShift, normalizeRecipientEmail, validShift, type ChecklistRecipient, type Site, type SiteShift } from "@fts/core";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { Note, Panel } from "../components/ui";
import type { ContactCandidate, EffectiveChecklistRecipients } from "../data/provider";
import { useI18n } from "../i18n";
import { formatDays } from "../lib/alarms";
import { sitePath } from "../lib/fleet";
import { canManageFleet, isTenantAdmin } from "../lib/permissions";
import { useSession, type TenantData } from "../state/session";

const WEEK = [1, 2, 3, 4, 5, 6, 7] as const;
const DEFAULT_TZ = "Europe/Istanbul";

type Loaded = {
  shifts: SiteShift[];
  recipients: ChecklistRecipient[];
  effective: EffectiveChecklistRecipients;
  candidates: ContactCandidate[] | null;
};
type Modal = { kind: "shift"; shift: SiteShift | null } | { kind: "deleteShift"; shift: SiteShift } | { kind: "member" } | { kind: "external" } | { kind: "remove"; recipient: ChecklistRecipient } | null;

/**
 * Site → "Vardiya öncesi kontrol listesi" (ADR 0016): shifts (own or inherited; every shift start needs a
 * checklist) and who receives checklist alarms (console members and, entered by admins, external
 * addresses). Read: everyone who sees the site; write: fleet managers+ (external addresses: admins).
 */
export function SiteChecklistPanel({ data, site }: { data: TenantData; site: Site }) {
  const { t } = useI18n();
  const { provider, tenant, role } = useSession();
  const [state, setState] = useState<Loaded | null>(null);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const tenantId = tenant?.id;
  const canEdit = canManageFleet(role);
  const admin = isTenantAdmin(role);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [shifts, recipients, effective, candidates] = await Promise.all([
        provider.listShifts(tenantId),
        provider.listChecklistRecipients(tenantId),
        provider.getEffectiveChecklistRecipients(site.id),
        canEdit ? provider.listContactCandidates(site.id).catch(() => null) : Promise.resolve(null),
      ]);
      setState({ shifts, recipients, effective, candidates });
      setError(false);
    } catch {
      setError(true);
    }
  }, [provider, tenantId, site.id, canEdit]);

  useEffect(() => {
    void load();
  }, [load]);

  const eff = useMemo(() => (state ? effectiveShifts(data.sites, state.shifts, site.id) : { shifts: [], sourceSiteId: null }), [state, data.sites, site.id]);
  const inheritedLabel = (id: string | null) =>
    id && data.sites.some((s) => s.id === id) ? t("siteChecklist.inherited", { site: sitePath(data.sites, id).map((s) => s.name).join(" / ") }) : t("siteChecklist.inheritedHidden");

  if (error)
    return (
      <Panel title={t("siteChecklist.title")}>
        <p className="form-error">{t("siteChecklist.loadError")}</p>
      </Panel>
    );
  if (!state)
    return (
      <Panel title={t("siteChecklist.title")}>
        <span className="spinner" aria-hidden="true" />
      </Panel>
    );

  const own = state.recipients.filter((r) => r.siteId === site.id);
  const candidateName = (userId: string) => state.candidates?.find((c) => c.userId === userId)?.displayName ?? state.effective.recipients.find((r) => r.userId === userId)?.displayName ?? t("common.empty");
  const recipientLabel = (r: ChecklistRecipient) => (r.userId ? candidateName(r.userId) : `${r.email}${r.label ? ` · ${r.label}` : ""}`);

  return (
    <Panel title={t("siteChecklist.title")}>
      <section className="cl-section" aria-labelledby={`shifts-${site.id}`}>
        <div className="cl-section-head">
          <h3 id={`shifts-${site.id}`} className="cl-section-title">
            {t("siteChecklist.shiftsTitle")}
          </h3>
          {canEdit && (
            <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "shift", shift: null })}>
              <Icon name="plus" size={15} />
              {t("siteChecklist.addShift")}
            </button>
          )}
        </div>
        <p className="panel-text muted small">{t("siteChecklist.shiftsHint")}</p>
        {eff.shifts.length === 0 ? (
          <p className="panel-text">{t("siteChecklist.noShifts")}</p>
        ) : (
          <>
            {eff.sourceSiteId !== site.id && <p className="muted small">{inheritedLabel(eff.sourceSiteId)}</p>}
            <ul className="cl-shifts">
              {eff.shifts.map((s) => (
                <li key={s.id}>
                  <span className="strong">{s.name}</span>
                  <span className="mono">
                    {s.start} – {isOvernightShift(s) ? t("siteChecklist.overnight", { time: s.end }) : s.end}
                  </span>
                  <span className="muted small">
                    {formatDays(t, s.weekdays)}
                    {s.tz !== DEFAULT_TZ ? ` · ${s.tz}` : ""}
                  </span>
                  {canEdit && s.siteId === site.id && (
                    <span className="cl-row-actions">
                      <button type="button" className="link-button" onClick={() => setModal({ kind: "shift", shift: s })}>
                        {t("common.edit")}
                      </button>
                      <button type="button" className="link-button button-danger-text" onClick={() => setModal({ kind: "deleteShift", shift: s })}>
                        {t("siteChecklist.deleteShift")}
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="cl-section" aria-labelledby={`recipients-${site.id}`}>
        <div className="cl-section-head">
          <h3 id={`recipients-${site.id}`} className="cl-section-title">
            {t("siteChecklist.recipientsTitle")}
          </h3>
          <span className="cl-row-actions">
            {canEdit && (
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "member" })}>
                <Icon name="user" size={15} />
                {t("siteChecklist.addMember")}
              </button>
            )}
            {admin && (
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "external" })}>
                <Icon name="mail" size={15} />
                {t("siteChecklist.addExternal")}
              </button>
            )}
          </span>
        </div>
        <p className="panel-text muted small">{t("siteChecklist.recipientsHint")}</p>
        <h4 className="cl-subtitle">{t("siteChecklist.applies")}</h4>
        {state.effective.sourceSiteId === null && <p className="panel-text">{t("siteChecklist.noRecipients")}</p>}
        {state.effective.recipients.length > 0 && (
          <>
            {state.effective.sourceSiteId && state.effective.sourceSiteId !== site.id && (
              <p className="muted small">{inheritedLabel(state.effective.sourceSiteId)}</p>
            )}
            {state.effective.externalSourceSiteId && state.effective.externalSourceSiteId !== site.id && state.effective.externalSourceSiteId !== state.effective.sourceSiteId && (
              <p className="muted small">
                {t("siteChecklist.external")}: {inheritedLabel(state.effective.externalSourceSiteId)}
              </p>
            )}
            <ul className="cl-recipients">
              {state.effective.recipients.map((r) => (
                <li key={r.recipientId}>
                  {r.userId ? (
                    <span>{r.displayName ?? t("common.empty")}</span>
                  ) : (
                    <span>
                      <span className="badge tone-info cl-inline-badge">{t("siteChecklist.external")}</span> {r.email ?? r.displayName ?? t("siteChecklist.external")}
                      {r.email && r.displayName ? <span className="muted small"> · {r.displayName}</span> : null}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        {canEdit && own.length > 0 && (
          <>
            <h4 className="cl-subtitle">{t("siteChecklist.ownList")}</h4>
            <ul className="cl-recipients">
              {own.map((r) => (
                <li key={r.id}>
                  <span>
                    {r.email && <span className="badge tone-info cl-inline-badge">{t("siteChecklist.external")}</span>} {recipientLabel(r)}
                  </span>
                  {(r.email ? admin : canEdit) && (
                    <button type="button" className="link-button button-danger-text" onClick={() => setModal({ kind: "remove", recipient: r })}>
                      {t("siteChecklist.remove")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        <Note>{t("siteChecklist.externalHint")}</Note>
        {canEdit && !admin && <p className="muted small">{t("siteChecklist.externalAdminOnly")}</p>}
      </section>

      {modal?.kind === "shift" && <ShiftDialog site={site} shift={modal.shift} onClose={() => setModal(null)} onSaved={async () => (setModal(null), await load())} />}
      {modal?.kind === "deleteShift" && (
        <ConfirmDialog
          title={t("siteChecklist.deleteShift")}
          confirmLabel={t("siteChecklist.deleteShift")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await provider.deleteShift(modal.shift.id);
            setModal(null);
            await load();
          }}
        >
          {t("siteChecklist.deleteShiftText", { name: modal.shift.name })}
        </ConfirmDialog>
      )}
      {(modal?.kind === "member" || modal?.kind === "external") && (
        <RecipientDialog
          site={site}
          external={modal.kind === "external"}
          candidates={(state.candidates ?? []).filter((c) => !own.some((r) => r.userId === c.userId))}
          onClose={() => setModal(null)}
          onSaved={async () => (setModal(null), await load())}
        />
      )}
      {modal?.kind === "remove" && (
        <ConfirmDialog
          title={t("siteChecklist.removeTitle")}
          confirmLabel={t("siteChecklist.remove")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await provider.deleteChecklistRecipient(modal.recipient.id);
            setModal(null);
            await load();
          }}
        >
          {t("siteChecklist.removeText", { name: recipientLabel(modal.recipient) })}
        </ConfirmDialog>
      )}
    </Panel>
  );
}

function ShiftDialog({ site, shift, onClose, onSaved }: { site: Site; shift: SiteShift | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [name, setName] = useState(shift?.name ?? "");
  const [start, setStart] = useState(shift?.start ?? "08:00");
  const [end, setEnd] = useState(shift?.end ?? "16:00");
  const [days, setDays] = useState<number[]>(shift?.weekdays ?? [1, 2, 3, 4, 5]);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const valid = validShift({ name, start, end, weekdays: days });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await provider.saveShift({ id: shift?.id, tenantId: site.tenantId, siteId: site.id, name: name.trim(), start, end, weekdays: days, tz: shift?.tz ?? DEFAULT_TZ });
      await onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={shift ? t("siteChecklist.editShift") : t("siteChecklist.addShift")}
      description={site.name}
      onClose={() => !busy && onClose()}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <FormField id="shift-name" label={t("siteChecklist.shiftName")}>
        <input value={name} maxLength={CHECKLIST_LIMITS.shiftNameMax} onChange={(e) => setName(e.target.value)} {...fieldAria("shift-name", {})} />
      </FormField>
      <div className="form-row">
        <FormField id="shift-start" label={t("siteChecklist.start")}>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} {...fieldAria("shift-start", {})} />
        </FormField>
        <FormField id="shift-end" label={t("siteChecklist.end")} hint={end && start && end < start ? t("siteChecklist.overnight", { time: end }) : undefined}>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} {...fieldAria("shift-end", { hint: end < start })} />
        </FormField>
      </div>
      <fieldset className="fieldset">
        <legend>{t("siteChecklist.days")}</legend>
        <div className="cl-days">
          {WEEK.map((d) => (
            <label key={d} className="check-row">
              <input type="checkbox" checked={days.includes(d)} onChange={(e) => setDays((xs) => (e.target.checked ? [...xs, d] : xs.filter((x) => x !== d)))} />
              <span>{formatDays(t, [d])}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {touched && !valid && <p className="field-error">{t("error.invalid_shift")}</p>}
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function RecipientDialog({ site, external, candidates, onClose, onSaved }: { site: Site; external: boolean; candidates: ContactCandidate[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [userId, setUserId] = useState(candidates[0]?.userId ?? "");
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const emailOk = !external || normalizeRecipientEmail(email) !== null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (busy || !emailOk || (!external && !userId)) return;
    setBusy(true);
    setError(null);
    try {
      await provider.addChecklistRecipient({ tenantId: site.tenantId, siteId: site.id, userId: external ? null : userId, email: external ? email : null, label: external ? label.trim() || null : null });
      await onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={external ? t("siteChecklist.addExternal") : t("siteChecklist.addMember")}
      description={site.name}
      onClose={() => !busy && onClose()}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || (!external && candidates.length === 0)}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      {external ? (
        <>
          <FormField id="rcp-email" label={t("siteChecklist.email")} error={touched && !emailOk ? t("error.invalid_recipient") : null}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} {...fieldAria("rcp-email", { error: touched && !emailOk ? "x" : null })} />
          </FormField>
          <FormField id="rcp-label" label={t("siteChecklist.label")} hint={t("siteChecklist.labelHint")} optional>
            <input value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} {...fieldAria("rcp-label", { hint: true })} />
          </FormField>
          <Note>{t("siteChecklist.externalHint")}</Note>
        </>
      ) : (
        <FormField id="rcp-user" label={t("siteChecklist.member")}>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} {...fieldAria("rcp-user", {})}>
            {candidates.map((c) => (
              <option key={c.userId} value={c.userId}>
                {c.displayName} · {t(`role.${c.role}`)}
              </option>
            ))}
          </select>
        </FormField>
      )}
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}
